// src/pages/black-friday-setup.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ExternalLink, Play, Copy } from "lucide-react";
import { fetchAllBFData, DEV_FALLBACK_BF_WEB_APP_URL } from "@/lib/black-friday-data-utils";
import { useBFAccounts } from "@/contexts/black-friday-account-context";
import { handleCopyBFScript } from "@/lib/black-friday-script-template";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";

const DEBUG = import.meta.env.DEV;
const TEMPLATE_URL = "https://docs.google.com/spreadsheets/d/14icbfYptNgi5FcWbHX0vVpSGB8_Cc6RtT-fqaUtipbk/copy";

export default function BlackFridaySetupPage() {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const { toast } = useToast();
  const { addAccount, accounts, switchAccount } = useBFAccounts();

  // Redirect to dashboard if accounts already exist
  useEffect(() => {
    if (accounts.length > 0) {
      navigate("/bf", { replace: true });
    }
  }, [accounts.length, navigate]);

  // Setup state - starts at step 1
  const [setupState, setSetupState] = useState({
    sheetUrl: "",
    webAppUrl: "",
    currentStep: DEBUG ? 3 : 1,
    scriptCopied: DEBUG, // Auto-mark as copied in dev mode
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Copy script helper
  const copyScript = async () => {
    try {
      const success = await handleCopyBFScript(setupState.sheetUrl);
      if (success) {
        setSetupState((prev) => ({ ...prev, scriptCopied: true }));
        toast({
          title: "Script Copied",
          description: "The Black Friday script has been copied to your clipboard",
        });
      } else {
        throw new Error("Failed to copy");
      }
    } catch (err) {
      console.error("Failed to copy script:", err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to copy script",
      });
    }
  };

  // Handle web app URL submission
  const handleLoadData = async () => {
    if (!setupState.webAppUrl) {
      setError("Please enter your Web App URL");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch data to validate URL and get account CID and name
      const data = await fetchAllBFData(setupState.webAppUrl);

      // Extract account CID and name from dashboard data
      const accountCID = data.dashboard?.accountCID || "";
      const accountName = data.dashboard?.accountName || "Unknown Account";

      if (!accountCID) {
        setError("Could not retrieve account Customer ID from the sheet. Please ensure your script has run at least once.");
        return;
      }

      // Check if this account already exists (by CID, name, or URL)
      const existingAccount = accounts.find(
        (acc) =>
          (acc.accountCID && acc.accountCID === accountCID) ||
          acc.accountName === accountName ||
          acc.webAppUrl === setupState.webAppUrl
      );

      if (existingAccount) {
        // Account already exists - switch to it and navigate to dashboard
        console.log(`Account "${accountName}" (CID: ${accountCID}) already exists, switching to it and navigating to dashboard`);
        switchAccount(existingAccount.id);
        toast({
          title: "Account Found",
          description: `"${accountName}" is already connected. Taking you to your dashboard.`,
        });
        navigate("/bf");
        return;
      }

      // Add account to context (this saves to localStorage and Clerk)
      await addAccount(accountCID, accountName, setupState.webAppUrl);

      // Navigate to dashboard
      navigate("/bf");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load data";
      setError(errorMessage);
      console.error("Error loading BF data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Define steps array
  const steps = [
    {
      number: 1,
      title: "Create Sheet",
      description: "Copy template and paste sheet URL",
      instructions: (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Create a copy of the Black Friday template sheet. Set it so 'Anyone with link' can edit it (required for the script to work). Then paste the URL below & click Continue.
          </p>
          <Button
            onClick={() => window.open(TEMPLATE_URL, "_blank")}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <ExternalLink className="h-4 w-4" />
            <span>Copy Black Friday Template Sheet</span>
          </Button>
          <Input
            value={setupState.sheetUrl}
            onChange={(e) =>
              setSetupState((prev) => ({
                ...prev,
                sheetUrl: e.target.value,
              }))
            }
            placeholder="Paste your sheet URL here"
          />
          <Button
            onClick={() =>
              setSetupState((prev) => ({ ...prev, currentStep: 2 }))
            }
            disabled={!setupState.sheetUrl.includes("sheet")}
          >
            Continue
          </Button>
        </div>
      ),
    },
    {
      number: 2,
      title: "Install Script",
      description: "Copy and run the Black Friday script",
      instructions: (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Copy the script (created with your sheet URL) and paste it into your Google Ads account. Authorize & run it once to verify it works. The sheet will fill with BF data for 2025, 2024, and 2023. Then schedule it to run daily.
          </p>
          <p className="text-xs text-muted-foreground italic">
            Need help with Google Ads Scripts? Visit{" "}
            <a href="https://8020agent.com/scripts" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
              8020agent.com/scripts
            </a>{" "}
            for a 14min course.
          </p>
          <Button
            onClick={copyScript}
            variant={setupState.scriptCopied ? "ghost" : "default"}
            className="flex items-center space-x-2"
          >
            <Copy className="h-4 w-4" />
            <span>{setupState.scriptCopied ? "Script Copied!" : "Copy Script to Clipboard"}</span>
          </Button>
          <Button
            onClick={() =>
              setSetupState((prev) => ({ ...prev, currentStep: 3 }))
            }
            disabled={!setupState.scriptCopied}
          >
            Continue
          </Button>
        </div>
      ),
    },
    {
      number: 3,
      title: "Deploy as Web App",
      description: "Get your Web App URL and connect",
      instructions: (
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            <p className="font-medium mb-2">To get your Web App URL:</p>
            <ol className="list-decimal list-inside space-y-0.5 text-xs ml-2">
              <li>Open your Google Sheet → <strong>Extensions</strong> → <strong>Apps Script</strong></li>
              <li>Click <strong>Deploy</strong> → <strong>New deployment</strong></li>
              <li>Click gear icon ⚙️ next to "Select type" → Choose <strong>Web app</strong></li>
              <li>Set "Execute as" to <strong>Me</strong> and "Who has access" to <strong>Anyone</strong></li>
              <li>Click <strong>Deploy</strong> and copy the <strong>Web App URL</strong></li>
            </ol>
          </div>
          <Input
            type="url"
            placeholder="https://script.google.com/macros/s/..."
            value={setupState.webAppUrl}
            onChange={(e) =>
              setSetupState((prev) => ({
                ...prev,
                webAppUrl: e.target.value,
              }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isLoading) {
                handleLoadData();
              }
            }}
            disabled={isLoading}
          />
          {DEBUG && (
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSetupState((prev) => ({ ...prev, webAppUrl: DEV_FALLBACK_BF_WEB_APP_URL }));
                }}
                disabled={isLoading}
              >
                Use Sample Data (Dev)
              </Button>
            </div>
          )}
          {!isSignedIn && (
            <p className="text-sm text-muted-foreground">
              You'll need to sign in or create an account to connect your Black Friday account.
            </p>
          )}
          <Button
            onClick={handleLoadData}
            disabled={isLoading || !setupState.webAppUrl || !setupState.webAppUrl.includes("macros")}
            className="min-w-[160px]"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Add Account
              </>
            )}
          </Button>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="container max-w-4xl mx-auto py-6 px-4">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <img src="/logo-light.png" alt="8020agent" className="h-8 w-auto" />
          </div>
          {!isSignedIn && (
            <Button size="sm" asChild>
              <a href="/auth/sign-in">Sign In / Sign Up</a>
            </Button>
          )}
        </div>
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-2">
            Black Friday 2025 vs 2024 Dashboard
          </h1>
          <p className="text-base text-muted-foreground">
            Compare your Black Friday performance across years
          </p>
        </div>
      </div>

      {/* Setup Steps */}
      <Card className="mb-6">
        <CardHeader className="pb-4">
          <CardTitle>Setup Wizard</CardTitle>
          <CardDescription>
            Let's get your Black Friday data connected
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {steps.map((step) => (
            <div
              key={step.number}
              className={cn(
                "space-y-3",
                step.number !== setupState.currentStep && "opacity-50",
              )}
            >
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-center",
                    step.number === setupState.currentStep &&
                      "bg-primary text-primary-foreground border-primary",
                    step.number !== setupState.currentStep &&
                      "border-muted-foreground",
                  )}
                >
                  {step.number}
                </div>
                <div>
                  <h3 className="font-semibold">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {step.description}
                  </p>
                </div>
              </div>

              {step.number === setupState.currentStep && (
                <div className="ml-11">{step.instructions}</div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Auth CTA */}
      {!isSignedIn && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle>Save Your Accounts</CardTitle>
            <CardDescription>
              Sign in or create a free account to save multiple Black Friday accounts.
              Next time you visit, your accounts will load automatically.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Button asChild>
                <a href="/auth/sign-in">Sign In</a>
              </Button>
              <Button variant="outline" asChild>
                <a href="/auth/sign-up">Create Account</a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
